import React, { ReactElement, useEffect } from 'react';
import { SvgIcon } from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import BasicModal from '../../../../modules/modals/BasicModal';
import JoinActionButton from '../../JoinActionButton';
import {
  Description,
  HeadlineContainer,
  ListDescription,
  StyledContainer,
  Title,
  TryAgainDescription,
  TryAgainHeadline,
  UlContainer,
  customTextStyle,
  selfieWallContentStyle,
  tryAgainModalStyle,
} from '../SelfieVerificationStyle';
import { CloseBtn, DashlineSeparator, closeButton } from '../../../common/CommonStyle';
import useMediaBreakpoints from '../../../../hooks/useMediaBreakpoints';
import {
  facelivenessSkipAction,
  setFaceLivenessStatus,
  setShowSelfieVerificationWall,
  updateProfileLivenessStatus,
} from '../../../../actions/profileActions';
import JoinSkipButton from '../../JoinSkipButton';
import colors from '../../../../theme/colors';
import MagicQRCode from '../CameraPermission/QRCode/MagicQRCode';
import { PUSHER } from '../../../../config/constants';
import { withPusher } from '../../../../contexts/PusherContext';
import { isDarkMode } from '../../../../utils/helpers';
import { getTextColor } from '../../../../style';
import MasterConfig from 'config/Master';

interface TryAgainModalProps {
  hash_id: string;
  showTryAgainModal: boolean;
  isJoinFlow: boolean;
  handleTryAgainClose: () => void;
  showSkippable: boolean;
  bindListener: (listener: () => void) => void;
  stopListening: (listener: () => void) => void;
  onRealtimeEvent: (event: string, callback: (data: any) => void) => void;
  isError?: boolean;
}

const TryAgainModal: React.FC<TryAgainModalProps> = ({
  hash_id,
  showTryAgainModal,
  isJoinFlow,
  handleTryAgainClose,
  showSkippable,
  bindListener,
  stopListening,
  onRealtimeEvent,
  isError = true,
}): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();

  useEffect(() => {
    bindListener && bindListener(listen);

    return () => {
      stopListening && stopListening(listen);
    };
  }, [showTryAgainModal]);

  const listen = () => {
    const { EVENTS } = PUSHER;

    onRealtimeEvent(EVENTS.ACCOUNT_LIVENESS_ALERT, data => {
      if (MasterConfig.APP_ENV !== 'production') {
        console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_LIVENESS_ALERT}`, data);
      }

      const query = new URLSearchParams(window.location.search);
      const source = query.get('source');

      if (showTryAgainModal && !source && data?.data?.status === 'pending') {
        dispatch(setShowSelfieVerificationWall(false));
        dispatch(setFaceLivenessStatus({ status: data?.data?.status, recommendation: data?.data?.recommendation }));
        dispatch(updateProfileLivenessStatus());
      }
    });
  };

  const handleClose = () => {
    if (showSkippable) {
      dispatch(facelivenessSkipAction(hash_id));
    }

    if (!isJoinFlow) {
      dispatch(setShowSelfieVerificationWall(false));
      dispatch(updateProfileLivenessStatus());
    }

    dispatch(setFaceLivenessStatus(null));
    handleTryAgainClose();
  };

  const handleTryAgainClick = () => {
    dispatch(setShowSelfieVerificationWall(false));
    dispatch(setFaceLivenessStatus(null));
    setTimeout(() => {
      dispatch(setShowSelfieVerificationWall(true));
    }, 100);
  };

  return (
    <BasicModal
      isOpen={showTryAgainModal}
      hideClose={true}
      customStyle={{
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        content: { ...tryAgainModalStyle.content, backgroundColor: !isJoinFlow && isDarkMode() ? '#041a2a' : '#fff' },
        overlay: tryAgainModalStyle.overlay,
      }}
      customContentStyle={selfieWallContentStyle}
      onClose={handleClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      {showSkippable && (
        <CloseBtn
          onClick={handleClose}
          style={{ top: isMobile ? '-50px' : '-25px', right: isMobile ? '-18px' : '-42px' }}
        >
          <SvgIcon component={CancelRounded} style={closeButton} />
        </CloseBtn>
      )}
      <HeadlineContainer isJoinFlow={true}>
        <Title>Selfie Verification</Title>
        <DashlineSeparator style={{ width: '100%' }} />
        <TryAgainHeadline isJoinFlow={isJoinFlow}>Uh oh. Verification failed.</TryAgainHeadline>
        <Description isJoinFlow={isJoinFlow}>
          {isError ? `We have a problem. Your selfie was not captured, please try again.` : `Let’s try this again.`}
        </Description>
        <DashlineSeparator style={{ width: '100%' }} />
      </HeadlineContainer>
      {!isError && (
        <>
          <TryAgainDescription isJoinFlow={isJoinFlow}>Please ensure:</TryAgainDescription>
          <UlContainer>
            <ListDescription isJoinFlow={isJoinFlow}>
              You’re in a well-lit area with your face clearly visible.
            </ListDescription>
            <ListDescription isJoinFlow={isJoinFlow}>
              Keep the camera steady and avoid any movement during the capture process.
            </ListDescription>
          </UlContainer>
          <DashlineSeparator style={{ marginTop: 10, width: '100%' }} />
        </>
      )}
      <StyledContainer>
        <div style={{ textAlign: 'center' }}>
          <JoinActionButton
            label={isError ? 'Try Again on this device' : 'Try Again'}
            isDefault={true}
            onClick={handleTryAgainClick}
            customStyle={{ marginBottom: 15, minWidth: '190px', borderRadius: 4, width: 'auto' }}
          />
          {showSkippable && (
            <JoinSkipButton
              label="Close"
              onClick={handleClose}
              customStyle={{ fontSize: '14px', fontWeight: 400, color: colors.gray['1100'] }}
            />
          )}
        </div>
      </StyledContainer>
      <DashlineSeparator style={{ width: '100%' }} />
      <MagicQRCode
        customStyle={{ marginBottom: 0 }}
        customTextStyle={{
          ...customTextStyle,
          color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY,
          width: '257px',
        }}
        scanText={`Alternatively, scan this QR Code to try again on another device`}
        isJoinFlow={isJoinFlow}
        showLoadingBg={true}
        customLoaderStyle={{ position: 'fixed', margin: '-40px -50px', left: 'auto' }}
      />
    </BasicModal>
  );
};

export default withPusher(TryAgainModal);
